<template>
    <div class="w-full pt-48 pb-16 bg-white">
    <UiContentwrapper>
      <UiFullcolumn>
        <!-- Main grid structure for 3 columns (visible only on medium screens and up) -->
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-16 pb-20" :class="{'hasCta' : hasCta}">
          
          <!-- Left column with list for medium and large screens -->
          <div class="col-span-1 md:col-span-1 lg:col-span-1" @mouseleave="handleHover(null)">
            <ul class="my-2">
                
              <li 
                v-for="(menuitem, index) in menu.items" 
                :key="menu.title" 
                @mouseover="handleHover(menuitem)"
                class="menu-item py-1"                
              >
                <NuxtLink v-if="menuitem.title !== 'My Gravaa'"
                  class="large hover:underline underline-offset-8 cursor-pointer"
                  :class="{ 'underline': activeHover && activeHover.title === menuitem.title }"                   
                  :to="menuStore.stripDomain(menuitem.url).replace('/collections/', '/shop/')" 
                  @click="$emit('closeDropdown')"
                >
                  {{ menuitem.title }}
                </NuxtLink>
                <a v-else href="https://gravaa.myshopify.com/account">
                    My Gravaa
                </a>

              </li>
              
            </ul>
            
          </div>
  
          <!-- Right columns with category image and blog content (for medium and large screens) -->
            <div>
                            
                <MenuHoverItem v-if="hoverItem1.hoverObject" :hoverItem="hoverItem1.hoverObject" :hovertype="hoverItem1.type" :url="hoverItem1.url" @click="$emit('closeDropdown')"/>
            
            </div>
            <div>              
                <MenuHoverItem
                v-if="hoverItem2.hoverObject"
                :hoverItem="hoverItem2.hoverObject"
                :hovertype="hoverItem2.type"
                :url="hoverItem2.url"
                class="large"
                secondary
                @click="$emit('closeDropdown')"
                />            
            </div>
        </div>
        
      </UiFullcolumn>
    </UiContentwrapper>
  </div>
  </template>

  <script setup>
  import { useMenuStore } from "@/stores/menuStore";
  import { usePageStore } from "@/stores/pageStore";
  import { useProductStore } from "@/stores/productStore";
  import { useBlogStore } from '@/stores/blogStore';
  import { mapProductToGa4Item } from '@/utils/ga4Map';
  const menuStore = useMenuStore();
  const pageStore = usePageStore();
  const productStore = useProductStore();
  const blogStore = useBlogStore();
  const activeHover = ref(null);
  
  const { proxy } = useScriptGoogleTagManager({ id: 'GTM-TXPLJB8M' });
  

    const { menu } = defineProps({
    menu: {
        type: Object,
        required: true
    }
    });

    const { data: products } = await useAsyncData('collectionProducts', () => {
    return productStore.fetchCollectionProductsByHandle('all')
    });

    const { data: blogs, error } = await useAsyncData('blogs', async () => {
        await blogStore.fetchBlogs();
        return blogStore.blogs;
    });
    

    import { ref } from 'vue';

    const hoverItem1 = ref({
        type: '',
        hoverObject: {},        
        url: ''
    });

    const hoverItem2 = ref({
        type: '',
        hoverObject: {},        
        url: ''
    });


    const emit = defineEmits(['refresh', 'closeDropdown']);

    const hasCta = computed(() => {
        return hoverItem2.value.type != '';
    });

    onMounted(() => {
                
        if (menu && menu.items.length > 0) {
            handleHover(menu.items[0]);            
        }            
        
        // Track view_item_list for PRODUCT type items in menu
        try {
            const productMenuItems = menu.items.filter(item => item.type === 'PRODUCT');
            
            if (productMenuItems.length > 0) {
                const listId = 'menu_products';
                const listName = menu.title || 'Menu';
                
                let currency = 'EUR'; // Default currency
                
                // Create a lookup map for O(1) product access
                const productByHandle = new Map(
                    (products.value || []).map(p => [p.handle, p])
                );
                
                const items = productMenuItems
                    .map((menuItem, index) => {
                        // Find the product from the lookup map
                        const product = productByHandle.get(menuStore.getSlug(menuItem.url));
                        if (!product) {
                            console.debug(`Product not found for menu item: ${menuItem.title} (${menuItem.url})`);
                            return null;
                        }
                        
                        // Extract currency from the first product
                        if (index === 0) {
                            currency = 
                                product?.priceRange?.minVariantPrice?.currencyCode ??
                                product?.variants?.edges?.[0]?.node?.price?.currencyCode ??
                                'EUR';
                        }
                        
                        return mapProductToGa4Item({
                            product: product,
                            index: index,
                            listId: listId,
                            listName: listName
                        });
                    })
                    .filter(Boolean);
                
                if (items.length > 0) {
                    console.log('Pushing view_item_list for menu products to dataLayer', {
                        currency,
                        items
                    });
                    
                    proxy.dataLayer.push({
                        event: 'view_item_list',
                        ecommerce: {
                            currency,
                            items
                        },
                        pageUrl: typeof window !== 'undefined' ? window.location.href : ''
                    });
                }
            }
        } catch (err) {
            console.warn('Error while pushing view_item_list analytics for menu products', err);
        }
        
    });

    function clearHoverItems() {
        hoverItem1.value.type = '';
        hoverItem1.value.hoverObject = {};
        hoverItem2.value.type = '';
        hoverItem2.value.hoverObject = {};
    }
    
    async function handleHover(menuitem) {
        if(menuitem)
        activeHover.value = menuitem;        
       
        if(!menuitem){            
            return;
        }
        
        setHoverItem(hoverItem1, menuitem);
        // Check for first submenu used for CTS (högerpuff)
        
        if(menuitem.items.length > 0){  
            //Using first (and only) child as hoveritem to the right                    
                setHoverItem(hoverItem2, menuitem.items[0]);            
        }else{            
            // No child for this one, try to check if the first item has children
            if(menu.items[0].items.length > 0){
        
                setHoverItem(hoverItem2, menu.items[0].items[0]);
            }else{
                // No  children found – use support page as default
        
                setHoverItem(hoverItem2, {"__typename":"MenuItem","title":"Support","url":"/support","type":"PAGE"});
            }
        }        
        //emit('refresh');
    }

    async function setHoverItem(hoverItem, menuitem){
        try{
            hoverItem.value.url = menuStore.stripDomain(menuitem.url);
            hoverItem.value.type = menuitem.type;            
            hoverItem.value.hoverObject = null;
            switch (menuitem.type) {
                case 'PRODUCT':
                    let hoverProduct = products.value.find(product => product.handle === menuStore.getSlug(menuitem.url));
                    if (hoverProduct) {                        
                        hoverItem.value.hoverObject = hoverProduct;                                             
                    }                                                                                                         
                    break;

                case 'COLLECTION':
                    
                    //let hoverCollection = productStore.collections.find(collection => collection.handle === menuStore.getSlug(menuitem.url));
                    let hoverCollection = await productStore.fetchCollectionById(menuitem.resource.id);
                    if (hoverCollection) {                        
                        hoverItem.value.hoverObject = hoverCollection;                    
                    }                                                    
                    break;
                case 'COLLECTIONS':
                
                    //let allCollection = productStore.collections.find(collection => collection.handle === 'all');
                    let allCollection = await productStore.fetchCollectionById(menuitem.resource.id);
                    if (allCollection) {                        
                        hoverItem.value.hoverObject = allCollection;   
                        hoverItem.value.type = 'COLLECTION';                 
                    }                                                    
                    break;
                case 'PAGE':

                    // get the pagecontent
                    let pageContent = await pageStore.fetchPageByHandle(menuStore.getSlug(menuitem.url)); 
                                        
                    if (pageContent.coverImage) {   
                        
                        hoverItem.value.hoverObject = pageContent;
                    }else{
                        console.warn('no pageContent or cover image set for ' + menuitem.title + ' ' + menuitem.url);
                    }                
                    break;

                case 'BLOG':
                    
                    let theBlog = await blogStore.fetchBlogByHandle(menuStore.getSlug(menuitem.url));                    
                    if (theBlog) {     
                        let article = await blogStore.getArticleByHandle(theBlog.articles[0].handle);               
                        hoverItem.value.hoverObject = article;
                    }
                    break;
                case 'ARTICLE':
                    //let article = await blogStore.getArticleByHandle(menuStore.getSlug(menuitem.url));
                    console.log('ARTICLE');
                    let article = await blogStore.fetchArticleById(menuitem.resource.id);
                    if(article){                        
                        hoverItem.value.hoverObject = article;
                    }else{
                        console.warn('no article found for ' + menuitem.title + ' ' + menuitem.url);
                    }
                    break;
                default:
                    //clearHoverItems();
                    break;
            }
        }catch(e){
            console.log(e);
            console.log('unable to set hoveritem');
            console.log(menu);
        }
    }
  </script>

  <style scoped>
@reference "../../assets/css/main.css";
    .grid.hasCta{        
        background: linear-gradient(to right, #000000, #000000);
        background-size: 1px 100%;
        background-repeat: no-repeat;
        background-position: 67.6% 0;
    }

    /* Apply h4-like styling to the <li> elements */
.menu-item {
  @apply text-2xl mb-2 md:mb-2; /* Tailwind utility classes for text size and margin */
  font-variation-settings: 'wght' 420, 'wdth' 100, 'slnt' 0; /* Font variation settings */
}
  </style>